import React from "react";
import PropTypes from "prop-types";
import { Map } from "immutable";
import { Field, FormSection, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import {
  ACCOUNTING,
  CAN_ADD_COMPANIES,
  CAN_UPDATE_COMPANIES
} from "MODULES/users/constants";

import * as actions from "MODULES/companies/actions";
import {
  form,
  Form as AppForm,
  FormInput,
  Select,
  CurrencyField,
  Check,
  AutoCompleteField
} from "MODULES/shared/components/Forms";
import DashedSeparator from "MODULES/shared/components/DashedSeparator";
import { disabledUnless as authorized } from "MODULES/shared/decorators";
import { hasPermission, hasRole } from "MODULES/users/helpers";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import { Col } from "react-bootstrap";
import { arrayToProperCasedOptionsWithCurrentValue } from "LIB/forms";
import { useCurrentUser } from "APP_ROOT/hooks";
import api from "APP_ROOT/api";
import { withFieldAccessors } from "MODULES/shared/decorators/withFieldAccessors";
import validate from "./validate";
import AddressFields from "../AddressFields";
import {
  CUSTOMER_CREDIT_TERMS,
  VENDOR_CREDIT_TERMS,
  VENDOR_TERMS
} from "../../constants";

const AccountingFields = authorized(
  user =>
    hasRole(user.toJS(), ACCOUNTING) ||
    hasPermission(user.toJS(), [CAN_ADD_COMPANIES, CAN_UPDATE_COMPANIES])
)(({ disabled, getInitialFieldValue }) => {
  const paymentTerms =
    useCurrentUser().tenant.payment_terms || Object.keys(VENDOR_TERMS);
  return (
    <div>
      <DashedSeparator />
      <FormRow>
        <Col md={7}>
          <Field
            name={VENDOR_CREDIT_TERMS}
            component={Select}
            label="Vendor credit terms"
            disabled={disabled}
            options={arrayToProperCasedOptionsWithCurrentValue(
              paymentTerms,
              getInitialFieldValue(VENDOR_CREDIT_TERMS)
            )}
          />
        </Col>
        <Col md={5}>
          <CurrencyField
            name="vendor_credit_limit"
            label="Vendor credit limit"
            inputProps={{ disabled }}
          />
        </Col>
      </FormRow>
      <FormRow>
        <Col md={7}>
          <Field
            name={CUSTOMER_CREDIT_TERMS}
            component={Select}
            label="Customer credit terms"
            disabled={disabled}
            options={arrayToProperCasedOptionsWithCurrentValue(
              paymentTerms,
              getInitialFieldValue(CUSTOMER_CREDIT_TERMS)
            )}
          />
        </Col>
        <Col md={5}>
          <CurrencyField
            name="customer_credit_limit"
            label="Customer credit limit"
            inputProps={{ disabled }}
          />
        </Col>
      </FormRow>
      <DashedSeparator />
      <FormRow>
        <Col md={6}>
          <Field
            name="resale_num"
            component={FormInput}
            inputProps={{ disabled }}
            label="Resale #"
          />
        </Col>
        <Col md={6}>
          <Field
            name="vat_num"
            component={FormInput}
            inputProps={{ disabled }}
            label="VAT #"
          />
        </Col>
      </FormRow>
      <FormRow>
        <Col md={6}>
          <Field name="ein_number" component={FormInput} label="EIN #" />
        </Col>
        <Col md={6}>
          <Field name="tax_id_number" component={FormInput} label="TAX ID #" />
        </Col>
      </FormRow>
    </div>
  );
});

export class Form extends React.Component {
  state = {
    sameAsPrimaryAddress: true
  };

  searchVendors = locationId => params =>
    api.locations.quickbooks.vendors
      .search(locationId, params)
      .then(({ data }) => data.results);

  searchCustomers = locationId => params =>
    api.locations.quickbooks.customers
      .search(locationId, params)
      .then(({ data }) => data.results);

  tooglePrimaryAddress() {
    if (this.state.sameAsPrimaryAddress && this.props.primaryAddress) {
      const {
        country,
        care_of,
        line1,
        line2,
        city,
        state,
        postal_code
      } = this.props.primaryAddress.toJS();
      this.props.change("billing_address.country", country);
      this.props.change("billing_address.care_of", care_of);
      this.props.change("billing_address.line1", line1);
      this.props.change("billing_address.line2", line2);
      this.props.change("billing_address.city", city);
      this.props.change("billing_address.state", state);
      this.props.change("billing_address.postal_code", postal_code);
    } else {
      this.props.reset();
    }
    this.setState(currentState => ({
      sameAsPrimaryAddress: !currentState.sameAsPrimaryAddress
    }));
  }

  render() {
    const { company, setFieldValue } = this.props;

    return (
      <AppForm {...this.props}>
        <AppForm.Body>
          <div className="form-group">
            <div className="btn-group" data-toggle="buttons">
              <Field
                onChange={() => this.tooglePrimaryAddress()}
                name="same_as_primary_address"
                component={Check}
                label="Copy Primary Physical Address"
              />
            </div>
          </div>
          <AddressFields
            path="billing_address"
            country={this.props.country}
            region={this.props.region}
            formName="editBilling"
            showRegion
            resetState
            stateProvinceRequired
          />
          <AccountingFields
            getInitialFieldValue={this.props.getInitialFieldValue}
          />
          {useCurrentUser()
            .locations.filter(
              f => f.quickbook_realm_id || f.quickbooks_desktop_integrated
            )
            .map((l, idx) => [
              <FormRow>
                <Col sm={12}>
                  <FormSection name={`locations[${idx}]`}>
                    <Field
                      component={FormInput}
                      name="id"
                      type="hidden"
                      style={{ height: 0 }}
                    />
                    <Field
                      name={"quickbooks_customer"}
                      label={`Quickbooks Customer Account - ${l.entity_name}`}
                      placeholder="Search by typing a company's name"
                      component={AutoCompleteField}
                      onItemSelected={item =>
                        setFieldValue(`locations[${idx}].quickbooks_customer`)(
                          item || { id: "remove" }
                        )}
                      selectedItem={company.quickbooks_customer[l.id]}
                      search={this.searchCustomers(l.id)}
                      formatItem={result => result && result.company_name}
                    />
                  </FormSection>
                </Col>
              </FormRow>,
              <FormRow>
                <Col sm={12}>
                  <FormSection name={`locations[${idx}]`}>
                    <Field
                      component={FormInput}
                      n
                      ame="id"
                      type="hidden"
                      style={{ height: 0 }}
                    />
                    <Field
                      name={"quickbooks_vendor"}
                      label={`Quickbooks Vendor Account - ${l.entity_name}`}
                      placeholder="Search by typing a company's name"
                      component={AutoCompleteField}
                      onItemSelected={item =>
                        setFieldValue(`locations[${idx}].quickbooks_vendor`)(
                          item || { id: "remove" }
                        )}
                      selectedItem={company.quickbooks_vendor[l.id]}
                      search={this.searchVendors(l.id)}
                      formatItem={result => result && result.company_name}
                    />
                  </FormSection>
                </Col>
              </FormRow>
            ])}
        </AppForm.Body>
      </AppForm>
    );
  }
}

Form.propTypes = {
  country: PropTypes.string,
  primaryAddress: PropTypes.instanceOf(Map)
};

const selector = formValueSelector("editBilling");

export default connect(
  state => ({
    primaryAddress: state.companyDetail.getIn([
      "company",
      "primary_physical_address"
    ]),
    country: selector(state, "billing_address.country")
  }),
  { onSubmit: actions.update }
)(
  form({
    form: "editBilling",
    validate
  })(withFieldAccessors(Form))
);



// WEBPACK FOOTER //
// ./app/modules/companies/components/EditBilling/Form.jsx